/** Écran 1 — Programme : catalogue par catégories, KPI de départ, barre basse « Financer → ». */
import type { MeasureCategory } from "../../../engine/src/types.js";
import { baselinePoint, kpiBlock } from "../components/kpis.js";
import { measureCard } from "../components/measure-card.js";
import { proposalBox } from "../components/proposal-card.js";
import { srcButton } from "../components/source-info.js";
import { stepsHtml } from "./existe.js";
import { CATEGORY_LABELS } from "../content-types.js";
import { bn, costAsBalance, escapeHtml as e, int } from "../format.js";
import type { Store } from "../state.js";
import { CATEGORY_ICONS, icon } from "../icons.js";

export function renderProgramme(store: Store): string {
  const b = store.content.baseline;
  const st = store.state;
  const base = baselinePoint(b);
  const firstYear = b.meta.base_year + 1;
  const lastYear = b.meta.base_year + Math.max(1, store.content.params.mandate_years?.value ?? b.asso.trend.length);
  const cats = Object.keys(CATEGORY_LABELS) as MeasureCategory[];
  const count = st.programme.measure_ids.length;
  const catalogue = cats
    .map((c) => {
      const ms = store.content.measures.filter((m) => m.category === c);
      if (!ms.length) return "";
      const act = ms.filter((m) => st.programme.measure_ids.includes(m.id));
      const sum = act.length ? store.engine.needGap(store.ctx, { measure_ids: act.map((m) => m.id), hyp: st.programme.hyp }) : 0;
      const summary = act.length ? `${act.length} active${act.length > 1 ? "s" : ""} · ${sum >= 0 ? "coût" : "rend."} ${bn(Math.abs(sum))}` : `${ms.length} proposition${ms.length > 1 ? "s" : ""}`;
      const open = act.length > 0;
      return `<details class="cat" data-cat="${c}" ${open ? "open" : ""}><summary><span class="cl">${icon(CATEGORY_ICONS[c], 20)}${e(CATEGORY_LABELS[c])}</span><span class="sum">${summary}</span></summary>${ms.map((m) => measureCard(store, m)).join("")}${proposalBox(c, { isAdded: (id) => st.programme.measure_ids.includes(id) })}</details>`;
    })
    .join("");
  const origin = store.content.origin;
  const fixtureNote =
    origin.measures === "fixture"
      ? `<p class="hint warn">Catalogue provisoire : mesures du prototype, non chiffrées ni sourcées (badge « non chiffré »). Le catalogue sourcé (content/measures.json) le remplacera automatiquement.</p>`
      : "";
  const presets = store.content.presets.length
    ? `<div class="card presets"><h2>Partir d'un programme existant</h2>${store.content.presets
        .map((p) => {
          const loaded = p.measure_ids.length > 0 && p.measure_ids.every((id) => st.programme.measure_ids.includes(id));
          return `<div class="preset" data-preset-card="${e(p.id)}"><b>${e(p.label)}</b>${srcButton({ title: p.label, sources: p.sources, notes: p.note ? [p.note] : [] }, { inline: true })}<p class="sm">${e(p.description)}</p><button type="button" class="sec" data-action="load-preset" data-preset="${e(p.id)}"${loaded ? " disabled" : ""}>${loaded ? "Chargé — modifie-le librement" : "Charger ce programme"}</button></div>`;
        })
        .join("")}<p class="hint">Charger remplace ton programme et ton financement en cours. Le tendanciel du jeu est « à politique inchangée » : sans ces mesures.</p></div>`
    : "";
  return `<div class="card">
    ${stepsHtml(store, 1)}
    <h1>Ton programme pour ${firstYear}–${lastYear}</h1>
    <p class="mut">Ouvre une catégorie et coche les mesures que tu veux appliquer. À droite, ce que chaque mesure coûte (−) ou rapporte (+) chaque année. Tu peux aussi proposer ta propre mesure en bas de chaque catégorie.</p>
    <details class="more" id="start-kpis"><summary>Point de départ ${b.meta.base_year} (indicateurs)</summary>${kpiBlock(store, base, undefined, 0)}</details>
  </div>
  ${presets}<div class="card">
    <h2>Catalogue des mesures <small>(${int(count)} en vigueur)</small></h2>
    <p class="hint">Une mesure est décrite par son contenu, jamais par son auteur. Coût « − » = creuse le solde, « + » = rapporte.</p>
    ${fixtureNote}
    ${catalogue}
  </div>
  <div class="card row"><button type="button" class="sec" data-action="reset">Recommencer</button></div>`;
}

export function renderProgrammeBottom(store: Store): string {
  const st = store.state;
  const gap = store.engine.needGap(store.ctx, st.programme);
  return `<div class="in">
    <div class="nums">
      <div>Mesures en vigueur<b>${int(st.programme.measure_ids.length)}</b></div>
      <div>Effet sur le solde<b>${costAsBalance(gap)} Md€/an <span class="mut">en régime</span></b></div>
    </div>
    <button type="button" class="primary" data-action="go-financement" aria-label="Passer au financement">Financer</button>
  </div>${gap === 0 && st.programme.measure_ids.length === 0 ? `<p class="hint">Sans mesure, tu joues le budget tendanciel : c'est permis, et instructif.</p>` : ""}`;
}
